import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Clock, ArrowUpRight, Tag } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { api, img, fmtDate } from "../../lib/api";
import { useSeo, pageSeo, breadcrumbLd } from "../../lib/seo";
import { PageHero, Breadcrumbs, Empty, Spinner } from "../../components/site/Bits";
import NotFound from "./NotFound";

export function PostCard({ p, i = 0 }) {
  return (
    <article className="card group flex flex-col fade-up img-zoom" style={{ animationDelay: `${i * 60}ms` }} data-testid={`post-card-${p.slug}`}>
      <Link to={`/blog/${p.slug}`} className="block aspect-[16/10] overflow-hidden bg-[#F5F5F5]">
        {p.cover_image ? <img src={img(p.cover_image)} alt={p.cover_alt || p.title} loading="lazy" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-black" />}
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <p className="eyebrow">{p.category || "Journal"} · {fmtDate(p.published_at)}</p>
        <h3 className="font-display text-xl font-bold leading-snug"><Link to={`/blog/${p.slug}`} className="hover:underline underline-offset-4">{p.title}</Link></h3>
        {p.excerpt && <p className="text-sm text-[#666] line-clamp-3">{p.excerpt}</p>}
        <div className="mt-auto pt-4 border-t border-[#E5E5E5] flex items-center justify-between text-xs text-[#333]">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{p.reading_minutes || 1} min read</span>
          <Link to={`/blog/${p.slug}`} className="font-semibold inline-flex items-center gap-1 hover:underline underline-offset-4" data-testid={`read-post-${p.slug}`}>Read <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
    </article>
  );
}

export function Blog() {
  const { site } = useSite();
  const [sp, setSp] = useSearchParams();
  const tag = sp.get("tag") || "";
  const page = Number(sp.get("page") || 1);
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/public/posts", { params: { tag, page } }).then((r) => setData(r.data)); }, [tag, page]);
  useSeo(pageSeo(site, "blog", { title: "Blog", description: "Trail guides, trip stories, packing lists and travel tips from the Bhatkya team." }), [site]);
  const go = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); if (k !== "page") n.delete("page"); setSp(n); };
  return (
    <>
      <PageHero eyebrow="Journal" title="Blog" text="Trail guides, trip stories, packing lists and travel tips." image="/images/about.jpg" />
      <section className="py-16 lg:py-24 bg-white"><div className="container-x">
        {data?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-tags">
            <button onClick={() => go("tag", "")} className={`chip ${!tag ? "chip-solid" : ""}`}>All</button>
            {data.tags.map((t) => <button key={t} onClick={() => go("tag", t)} className={`chip ${tag === t ? "chip-solid" : ""}`} data-testid={`tag-${t}`}>{t}</button>)}
          </div>
        )}
        {!data ? <Spinner /> : !data.items.length ? <Empty title="No posts yet" text="Stories and guides will appear here once published." /> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">{data.items.map((p, i) => <PostCard key={p.id} p={p} i={i} />)}</div>
        )}
        {data?.pages > 1 && (
          <div className="flex justify-center gap-3 mt-12 text-sm" data-testid="blog-pager">
            <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => go("page", String(page - 1))}>Previous</button>
            <span className="self-center">Page {page} of {data.pages}</span>
            <button className="btn-secondary btn-sm" disabled={page >= data.pages} onClick={() => go("page", String(page + 1))}>Next</button>
          </div>
        )}
      </div></section>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { site } = useSite();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setP(null); setErr(false); api.get(`/public/posts/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  const brand = site?.brand?.name || "Bhatkya";
  useSeo(p ? {
    title: p.meta_title || p.title, description: p.meta_description || p.excerpt, canonical: p.canonical_url || undefined, robots: p.robots, ogImage: p.og_image || p.cover_image,
    jsonLd: [breadcrumbLd([{ name: "Home", to: "/" }, { name: "Blog", to: "/blog" }, { name: p.title, to: `/blog/${p.slug}` }]),
      { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.excerpt, datePublished: p.published_at, dateModified: p.updated_at, ...(p.cover_image && { image: img(p.cover_image) }),
        author: { "@type": "Organization", name: brand }, publisher: { "@type": "Organization", name: brand }, mainEntityOfPage: window.location.origin + `/blog/${p.slug}`, keywords: (p.tags || []).join(", ") }],
  } : { title: "Blog" }, [p, site]);
  if (err) return <NotFound />;
  if (!p) return <Spinner />;
  return (
    <article data-testid="blog-post">
      <PageHero eyebrow={`${p.category || "Journal"} · ${fmtDate(p.published_at)} · ${p.reading_minutes || 1} min read`} title={p.title} text={p.excerpt} image={p.cover_image} alt={p.cover_alt} />
      <div className="container-x pt-6"><Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Blog", to: "/blog" }, { name: p.title }]} /></div>
      <section className="py-12 lg:py-16 bg-white"><div className="container-x grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 lg:col-start-3">
          <p className="text-sm text-[#666] mb-8">By {brand}</p>
          <div className="prose-bw" data-testid="post-content" dangerouslySetInnerHTML={{ __html: p.content_html }} />
          {p.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-[#E5E5E5]" data-testid="post-tags"><Tag className="h-4 w-4 mt-0.5" />{p.tags.map((t) => <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="chip-muted hover:border-black hover:text-black">{t}</Link>)}</div>}
        </div>
      </div></section>
      {p.related?.length > 0 && (
        <section className="py-20 bg-[#F5F5F5] border-t border-[#E5E5E5]"><div className="container-x">
          <h2 className="section-title mb-10">Keep reading</h2>
          <div className="grid gap-6 md:grid-cols-3">{p.related.map((r, i) => <PostCard key={r.id} p={r} i={i} />)}</div>
        </div></section>
      )}
    </article>
  );
}
